import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faExclamationCircle,
  faRedo,
  faCalendarAlt,
  faReceipt,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";
import { formatDateOnly, parseLocalDateOnly } from "../../utils/DateUtils";
import "./BillMonthlyView.css";

const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(now.getFullYear() - 10, 0, 1);
  const d = new Date(end);
  while (d >= start) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      key,
      label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    });
    d.setMonth(d.getMonth() - 1);
  }
  return options;
};

const MONTH_OPTIONS = getMonthOptions();

const getCurrentMonthKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const BillMonthlyView = ({ onSelectBill }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(() => [getCurrentMonthKey()]);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const monthDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) {
        setMonthDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAllBills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const all = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await apiService.getBills({
          page,
          per_page: 100,
          user_id: userData.UserID || null,
          sort_by: "bill_date",
          sort_order: "DESC",
        });
        const list = res.bills || [];
        all.push(...list);
        const totalPages = res.pagination?.total_pages || 1;
        hasMore = page < totalPages;
        page += 1;
      }
      setBills(all);
    } catch (err) {
      setError(err.message || "Failed to fetch bills");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllBills();
  }, [fetchAllBills]);

  // Group by month (YYYY-MM) using local date to avoid timezone shift
  const byMonth = {};
  bills.forEach((bill) => {
    const dateStr = bill.bill_date || bill.created_at;
    if (!dateStr) return;
    const d = parseLocalDateOnly(dateStr) || new Date(dateStr);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!byMonth[key]) {
      byMonth[key] = { label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }), bills: [], total: 0 };
    }
    byMonth[key].bills.push(bill);
    byMonth[key].total += parseFloat(bill.total_amount) || 0;
  });
  let months = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0]));
  if (selectedMonths.length > 0) {
    const set = new Set(selectedMonths);
    months = months.filter(([key]) => set.has(key));
  }

  const filteredBillsCount = months.reduce((s, [, data]) => s + data.bills.length, 0);
  const filteredTotalAmount = months.reduce((s, [, data]) => s + data.total, 0);

  const toggleMonth = (key) => {
    setSelectedMonths((prev) => {
      if (prev.includes(key)) return prev.filter((m) => m !== key);
      return [...prev, key].sort();
    });
  };

  const selectAllMonths = () => setSelectedMonths([]);
  const selectCurrentMonthOnly = () => {
    setSelectedMonths([getCurrentMonthKey()]);
    setMonthDropdownOpen(false);
  };

  const getMonthFilterLabel = () => {
    if (selectedMonths.length === 0) return "All months";
    if (selectedMonths.length === 1) {
      const opt = MONTH_OPTIONS.find((o) => o.key === selectedMonths[0]);
      return opt ? opt.label : selectedMonths[0];
    }
    return `${selectedMonths.length} months`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => formatDateOnly(dateString);

  if (loading) {
    return (
      <div className="bill-monthly-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading bills...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bill-monthly-error">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <p>{error}</p>
        <button onClick={fetchAllBills} className="retry-btn">
          <FontAwesomeIcon icon={faRedo} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bill-monthly-container">
      <div className="bill-monthly-header">
        <h2>
          <FontAwesomeIcon icon={faCalendarAlt} /> Monthly view
        </h2>
        <button onClick={fetchAllBills} className="refresh-btn" title="Reload">
          <FontAwesomeIcon icon={faRedo} /> Refresh
        </button>
      </div>

      <div className="bill-monthly-filter-wrap" ref={monthDropdownRef}>
        <span className="bill-monthly-filter-label">
          <FontAwesomeIcon icon={faCalendarAlt} /> Month:
        </span>
        <div className="bill-monthly-dropdown-wrap">
          <button
            type="button"
            className="bill-monthly-dropdown-trigger"
            onClick={() => setMonthDropdownOpen((o) => !o)}
            aria-expanded={monthDropdownOpen}
          >
            <span>{getMonthFilterLabel()}</span>
            <span className="bill-monthly-dropdown-arrow">▼</span>
          </button>
          {monthDropdownOpen && (
            <div className="bill-monthly-dropdown-panel">
              <div className="bill-monthly-dropdown-actions">
                <button type="button" onClick={() => { selectAllMonths(); setMonthDropdownOpen(false); }}>
                  All months
                </button>
                <button type="button" onClick={selectCurrentMonthOnly}>
                  Current month only
                </button>
              </div>
              <div className="bill-monthly-dropdown-list">
                {MONTH_OPTIONS.map(({ key, label }) => (
                  <label key={key} className="bill-monthly-dropdown-option">
                    <input
                      type="checkbox"
                      checked={selectedMonths.includes(key)}
                      onChange={() => toggleMonth(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bill-monthly-summary">
        <div className="bill-monthly-summary-card">
          <FontAwesomeIcon icon={faReceipt} />
          <span className="bill-monthly-summary-value">{filteredBillsCount}</span>
          <span className="bill-monthly-summary-label">Total bills</span>
        </div>
        <div className="bill-monthly-summary-card">
          <FontAwesomeIcon icon={faDollarSign} />
          <span className="bill-monthly-summary-value">
            {formatCurrency(filteredTotalAmount)}
          </span>
          <span className="bill-monthly-summary-label">Total amount</span>
        </div>
      </div>

      {months.length === 0 ? (
        <div className="bill-monthly-empty">
          <FontAwesomeIcon icon={faCalendarAlt} />
          <p>No bills with dates found.</p>
        </div>
      ) : (
        <div className="bill-monthly-list">
          {months.map(([key, { label, bills: monthBills, total }]) => (
            <div key={key} className="bill-monthly-group">
              <div className="bill-monthly-group-header">
                <span className="bill-monthly-group-title">{label}</span>
                <span className="bill-monthly-group-meta">
                  {monthBills.length} bill{monthBills.length !== 1 ? "s" : ""} · {formatCurrency(total)}
                </span>
              </div>
              <div className="bill-monthly-group-bills">
                {monthBills.map((bill) => (
                  <div
                    key={bill.bill_id}
                    className="bill-monthly-bill-row"
                    onClick={() => onSelectBill && onSelectBill(bill.bill_id)}
                  >
                    <span className="bill-monthly-merchant">{bill.merchant_name || "Unknown"}</span>
                    <span className="bill-monthly-date">{formatDate(bill.bill_date)}</span>
                    <span className="bill-monthly-amount">{formatCurrency(bill.total_amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillMonthlyView;
